import { z } from "zod";

export const idSchema = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-zA-Z0-9_-]+$/);
export const nameSchema = z.string().trim().min(1).max(120);
export const modeSchema = z.enum([
  "single",
  "double",
  "pools",
  "king",
  "swiss",
]);
export const stages = [
  "pool",
  "swiss",
  "winner",
  "loser",
  "playoff",
  "semifinal",
  "final",
  "bronze",
] as const;
export const stageSchema = z.enum(stages);
export const scoringSchema = z
  .object({
    target: z.number().int().min(1).max(99),
    setsToWin: z.number().int().min(1).max(3),
    winByTwo: z.boolean(),
    cap: z.number().int().min(1).max(199).nullable(),
  })
  .refine(
    (rule) => rule.cap === null || rule.cap >= rule.target,
    "The cap cannot be below the target.",
  );
export const playerSchema = z.object({
  id: idSchema,
  name: nameSchema,
  points: z.number().min(0).max(1e9),
});
export const teamSchema = z.object({
  id: idSchema,
  name: nameSchema,
  players: z.tuple([playerSchema, playerSchema]),
  bringsNet: z.boolean(),
  seed: z.number().int().min(1).max(50),
  withdrawn: z.boolean().default(false),
});
export const pointsTableSchema = z.object({
  id: idSchema,
  name: nameSchema,
  awards: z
    .array(
      z.object({
        place: z.number().int().min(1).max(50),
        points: z.number().min(0).max(1e9),
      }),
    )
    .max(50),
});
export const windowSchema = z
  .object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    opens: z.string().regex(/^\d{2}:\d{2}$/),
    closes: z.string().regex(/^\d{2}:\d{2}$/),
  })
  .refine(
    (w) => w.opens < w.closes,
    "Court closing time must follow its opening time.",
  );
export const courtSchema = z.object({
  id: idSchema,
  name: nameSchema,
  netTeamId: idSchema.nullable(),
  windows: z.array(windowSchema).min(1).max(14),
});
export const configSchema = z.object({
  mode: modeSchema,
  poolCount: z.number().int().min(1).max(12),
  qualifiers: z.number().int().min(0).max(50),
  swissRounds: z.number().int().min(1).max(20),
  heatCount: z.number().int().min(1).max(20),
  heatMinutes: z.number().int().min(1).max(120),
  scoring: scoringSchema,
  stageScoring: z.partialRecord(stageSchema, scoringSchema),
  bronze: z.boolean(),
  releaseStages: z.array(stageSchema),
  notBefore: z.partialRecord(stageSchema, z.iso.datetime({ offset: true })),
  estimatedMatchMinutes: z.number().int().min(1).max(180),
  pointsTable: pointsTableSchema,
});
export const draftSchema = z.object({
  name: nameSchema,
  location: nameSchema,
  timezone: z
    .string()
    .max(100)
    .refine((value) => {
      try {
        new Intl.DateTimeFormat("en", { timeZone: value });
        return true;
      } catch {
        return false;
      }
    }, "Use an IANA timezone, such as America/Los_Angeles."),
  teams: z.array(teamSchema).min(8).max(50),
  courts: z.array(courtSchema).min(1).max(16),
  config: configSchema,
});

export type Mode = z.infer<typeof modeSchema>;
export type Stage = z.infer<typeof stageSchema>;
export type Scoring = z.infer<typeof scoringSchema>;
export type Player = z.infer<typeof playerSchema>;
export type Team = z.infer<typeof teamSchema>;
export type Court = z.infer<typeof courtSchema>;
export type Config = z.infer<typeof configSchema>;
export type PointsTable = z.infer<typeof pointsTableSchema>;
export type Draft = z.infer<typeof draftSchema>;
export type Pair = [number, number];
export type Slot =
  | { teamId: string }
  | { matchId: string; outcome: "winner" | "loser" }
  | { bye: true };

export interface Match {
  id: string;
  stage: Stage;
  poolId?: string;
  round: number;
  wave: number;
  slots: [Slot, Slot];
  teams: [string | null, string | null];
  status: "waiting" | "ready" | "live" | "complete";
  sets: Pair[];
  points: Pair;
  winnerId?: string;
  loserId?: string;
  resolution?: "played" | "bye" | "forfeit" | "retirement";
  courtId?: string;
  refereeId?: string;
  refereeSlot?: Exclude<Slot, { bye: true }>;
  refereeMode: "auto" | "team" | "self";
  needsRelease: boolean;
  released: boolean;
  notBefore?: string;
  startedAt?: string;
  completedAt?: string;
  revision: number;
  scoring?: Scoring;
}
export interface Pool {
  id: string;
  teamIds: string[];
  courtId: string;
}
export interface Heat {
  id: string;
  round: number;
  teamIds: string[];
  courtId: string;
  status: "ready" | "live" | "complete";
  totals?: Record<string, number>;
  startedAt?: string;
  completedAt?: string;
  revision: number;
}
export interface Withdrawal {
  teamId: string;
  replacementId?: string;
  matchId?: string;
  awardPlace?: number;
  at: string;
}
export interface Tournament extends Draft {
  id: string;
  version: number;
  nextMatchNumber: number;
  advancementIssue?: string;
  status: "draft" | "running" | "finished";
  published: boolean;
  pools: Pool[];
  matches: Match[];
  heats: Heat[];
  tieDecisions?: Array<{ key: string; winnerId: string }>;
  playoffGenerated: boolean;
  withdrawals: Withdrawal[];
  createdAt: string;
  awardedAt?: string;
}
export type Actor =
  { role: "admin" } | { role: "team"; teamId: string; tournamentId: string };

export class RuleError extends Error {
  constructor(
    message: string,
    public readonly code = "invalid",
  ) {
    super(message);
    this.name = "RuleError";
  }
}
export function ensure(
  condition: unknown,
  message: string,
  code = "invalid",
): asserts condition {
  if (!condition) throw new RuleError(message, code);
}
export function required<T>(
  value: T | undefined,
  message = "The selected item no longer exists.",
): T {
  ensure(value !== undefined, message, "not_found");
  return value;
}
export const defaultScoring: Scoring = {
  target: 21,
  setsToWin: 1,
  winByTwo: true,
  cap: null,
};
export function defaultConfig(pointsTable: PointsTable): Config {
  return {
    mode: "pools",
    poolCount: 2,
    qualifiers: 4,
    swissRounds: 4,
    heatCount: 3,
    heatMinutes: 15,
    scoring: { ...defaultScoring },
    stageScoring: {},
    bronze: false,
    releaseStages: ["semifinal", "final"],
    notBefore: {},
    estimatedMatchMinutes: 25,
    pointsTable,
  };
}
